import React, { forwardRef } from 'react'
import i18n from 'i18n'
import { Trans, useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { Theme } from 'features/ui/types'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
// @ts-ignore
import { sys } from '@netbasequid/theme'
// @ts-ignore
import { Skeleton, QuestionMarkOutlined, Tooltip } from '@netbasequid/core'
import {
  selectDisplayDashboardInfos,
  selectDashboardInfoStatus,
  selectIsFiltered,
  selectDashboardInfoSortingAndFilter,
  selectDashboardInfoCategory,
  selectCategoryIsTemplate
} from 'features/dashboardInfo/selectors'
import { DashboardInfo } from 'schemas/DashboardInfoSchema'
import { selectIsRoleBelowAnalyst } from 'features/config/selectors'
import { sendMessage } from 'utils/iframeUtil'
import LongListTable, { LongListColDefs } from 'components/visualization/Table/LongListTable'
import { setCategory } from 'features/dashboardInfo/dashboardInfoSlice'
import {
  StyledLoadingContainer,
  StyledNameContainer,
  StyledNameLink,
  StyledLabelContainer,
  StyledEmptyTable,
  StyledHeaderContentContainer,
  StyledHighlightingCellContainer,
  StyledHighlightingCell,
  StyledLearnMoreLink,
  StyledClickableText
} from './index.css'
import DashSymbol from './DashSymbol'
import StatusCircle from './StatusCircle'
import LongTextContainer from './LongTextContainer'

const HighlightingCellContent = forwardRef(({ children }: { children?: string }, ref) => {
  const { filter } = useAppSelector(selectDashboardInfoSortingAndFilter)

  let parts = [] as {
    text: string
    highlight: boolean
  }[]
  if (filter.keyword) {
    const matches = match(children!, filter.keyword!, { insideWords: true })
    parts = parse(children!, matches)
  }

  return (
    <StyledHighlightingCellContainer ref={ref as React.RefObject<HTMLDivElement>}>
      {parts.length === 0 ? (
        <>{children}</>
      ) : (
        parts.map((part, partIndex) => (
          <StyledHighlightingCell
            // eslint-disable-next-line react/no-array-index-key
            key={partIndex}
            style={{ display: 'inline-block', fontWeight: part.highlight ? 600 : 400 }}>
            {part.text}
          </StyledHighlightingCell>
        ))
      )}
    </StyledHighlightingCellContainer>
  )
})

const LabelCell = ({ cell }: { cell: DashboardInfo['labels'] }) => {
  const sysTheme = useSysTheme()

  return (
    <StyledLabelContainer>
      {cell?.length ? (
        <LongTextContainer color={sysTheme.text.primary} tooltipContent={<>{cell?.join(', ')}</>}>
          <HighlightingCellContent>{cell?.join(', ')}</HighlightingCellContent>
        </LongTextContainer>
      ) : (
        <DashSymbol />
      )}
    </StyledLabelContainer>
  )
}

const NameCell = ({ cell, row }: { cell: DashboardInfo['name']; row: DashboardInfo }) => {
  const mode = useDisplayTheme() as Theme
  const sysTheme = useSysTheme()
  const navigate = useNavigate()

  const handleDashboardItemClick = (id: number) => {
    navigate(`/dashboard/${id}`)
    sendMessage({ type: 'v4DashboardClick', data: id })
  }

  return (
    <StyledNameContainer>
      <LongTextContainer color={sysTheme.interaction.primary.default} tooltipContent={<>{cell}</>}>
        <StyledNameLink onClick={() => handleDashboardItemClick(row.id!)} mode={mode}>
          <HighlightingCellContent>{cell}</HighlightingCellContent>
        </StyledNameLink>
      </LongTextContainer>
    </StyledNameContainer>
  )
}

const DateCell = ({ cell }: { cell: DashboardInfo['lastViewedDatetime'] }) => {
  const sysTheme = useSysTheme()

  if (!cell) {
    return <DashSymbol />
  }

  const timestamp = Number(cell)
  const isValidTimestamp = !Number.isNaN(timestamp) && timestamp > 0

  if (!isValidTimestamp) {
    return <DashSymbol />
  }

  const formattedDate = dayjs(Number(cell)).format('MMM D, YYYY')

  return (
    <LongTextContainer color={sysTheme.text.primary} tooltipContent={<>{formattedDate}</>}>
      {formattedDate}
    </LongTextContainer>
  )
}

const EmptyTableContent = () => {
  const { t } = useTranslation()
  const mode = useDisplayTheme() as Theme
  const currentCategory = useAppSelector(selectDashboardInfoCategory)
  const categoryIsTemplate = useAppSelector(selectCategoryIsTemplate)
  const dispatch = useAppDispatch()

  if (categoryIsTemplate) {
    return (
      <Trans
        i18nKey="noTemplatesFoundLearnMore"
        components={{
          CustomLink: (
            <StyledLearnMoreLink mode={mode} href="https://help.quid.com/en/articles/8243202" />
          )
        }}
      />
    )
  }
  if (currentCategory === 'recentlyViewedDashboards') {
    return (
      <Trans
        i18nKey="noRecentlyViewedDashboards"
        components={{
          CustomLink: (
            <StyledClickableText
              mode={mode}
              onClick={() => dispatch(setCategory('allDashboards'))}
            />
          )
        }}
      />
    )
  }
  return <>{t('dashboardHomeEmptyTable')}</>
}

const baseColDefs: LongListColDefs<DashboardInfo, undefined, keyof DashboardInfo> = [
  {
    field: 'name',
    headerText: i18n.t('name'),
    customHeaderRender: () => <>{i18n.t('name')}</>,
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      displayTooltip: false,
      style: {
        width: '40%'
      }
    },
    customCellRender: (cell, { row }) => <NameCell cell={cell} row={row} />,
    sortable: true,
    accessorKey: 'name'
  },
  {
    field: 'lastViewedDatetime',
    headerText: i18n.t('lastViewed'),
    customHeaderRender: ({ theme }) => (
      <StyledHeaderContentContainer>
        {i18n.t('lastViewed')}
        <Tooltip mode={theme} content={i18n.t('dashboardHomeLastViewedHeaderTooltip')}>
          <QuestionMarkOutlined fontSize={14} color={sys[theme].text.placeholder} />
        </Tooltip>
      </StyledHeaderContentContainer>
    ),
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      style: {
        width: '10%'
      }
    },
    sortable: true,
    customCellRender: (cell) => <DateCell cell={cell} />,
    accessorKey: 'lastViewedDatetime'
  },
  {
    field: 'labels',
    headerText: i18n.t('label_other'),
    customHeaderRender: () => <>{i18n.t('label_other')}</>,
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      style: {
        width: '20%'
      }
    },
    sortable: true,
    customCellRender: (cell) => <LabelCell cell={cell} />,
    accessorKey: 'labels'
  },
  {
    field: 'createDatetime',
    headerText: i18n.t('dateCreated'),
    customHeaderRender: () => <>{i18n.t('dateCreated')}</>,
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      style: {
        width: '10%'
      }
    },
    sortable: true,
    customCellRender: (cell) => <DateCell cell={cell} />,
    accessorKey: 'createDatetime'
  },
  {
    field: 'updateDatetime',
    headerText: i18n.t('dateUpdated'),
    customHeaderRender: () => <>{i18n.t('dateUpdated')}</>,
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      style: {
        width: '10%'
      }
    },
    sortable: true,
    customCellRender: (cell) => <DateCell cell={cell} />,
    accessorKey: 'updateDatetime'
  },
  {
    field: 'liveReportEnabled',
    headerText: i18n.t('liveReport'),
    customHeaderRender: () => <>{i18n.t('liveReport')}</>,
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      style: {
        width: '3%'
      }
    },
    sortable: true,
    customCellRender: (cell) => <StatusCircle isEnabled={cell} />,
    accessorKey: 'liveReportEnabled'
  },
  {
    field: 'ownerInfo',
    headerText: i18n.t('createdBy'),
    customHeaderRender: () => <>{i18n.t('createdBy')}</>,
    cellProps: {
      textAlign: 'left',
      colSidePaddings: { left: '12px', right: '12px' },
      style: {
        width: '4%'
      }
    },
    sortable: true,
    customCellRender: (cell) => <StyledLabelContainer>{cell?.name}</StyledLabelContainer>,
    accessorKey: 'ownerInfo',
    sortingFn: (rowA, rowB) => {
      if (rowA.original?.ownerInfo?.name && rowB.original?.ownerInfo?.name) {
        return rowA.original?.ownerInfo?.name > rowB.original?.ownerInfo?.name ? 1 : -1
      }
      return 0
    }
  }
]

const getColDefs = (includeScheduledReport: boolean): LongListColDefs<DashboardInfo> => {
  const colDefs = [...baseColDefs]
  if (includeScheduledReport) {
    colDefs.splice(5, 0, {
      field: 'scheduledReportEnabled',
      headerText: i18n.t('scheduledReport'),
      cellProps: {
        textAlign: 'left',
        colSidePaddings: { left: '12px', right: '12px' },
        style: {
          width: '3%'
        }
      },
      sortable: true,
      customCellRender: (cell) => <StatusCircle isEnabled={cell} />,
      accessorKey: 'scheduledReportEnabled'
    })
  }
  return colDefs
}

const ListTable = () => {
  const mode = useDisplayTheme() as Theme
  const dashboardInfos = useAppSelector(selectDisplayDashboardInfos)
  const status = useAppSelector(selectDashboardInfoStatus)
  const isRoleBelowAnalyst = useAppSelector(selectIsRoleBelowAnalyst)
  const { t } = useTranslation()
  const isFiltered = useAppSelector(selectIsFiltered)

  const dashboardColDefs = getColDefs(!isRoleBelowAnalyst)

  if (status === 'loading') {
    return (
      <StyledLoadingContainer>
        <Skeleton mode={mode} variation="rectangle" width="100%" height="100%" animation="wave" />
      </StyledLoadingContainer>
    )
  }

  return (
    <>
      {dashboardInfos.length === 0 ? (
        <StyledEmptyTable mode={mode}>
          {isFiltered ? t('noResultsFound') : <EmptyTableContent />}
        </StyledEmptyTable>
      ) : (
        <LongListTable rowData={dashboardInfos} colDefs={dashboardColDefs} isDisplayFoot={false} />
      )}
    </>
  )
}

export default ListTable
